"use client"
import { LegacyMyTasksWidget } from '@/components/dashboard/legacy-my-tasks-widget'

import { useCallback, useEffect, useRef, useState } from "react"
import Link from "next/link"
import { IconAlertTriangle, IconCheck, IconChevronRight, IconListCheck } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"
import { TaskCompletionDialog } from "@/components/tasks/task-completion-dialog"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { usePermissions } from "@/hooks/use-permissions"
import { useAuthStore } from "@/stores/auth-store"
import { hasFlag } from "@/lib/feature-flags"
import { getTasks, type Task } from "@/lib/tasks-api"
import { cn } from "@/lib/utils"

function WorkflowMyTasksWidget() {
  const { can, isLoaded } = usePermissions()
  const user = useAuthStore((s) => s.user)
  const enabled = isLoaded && hasFlag(user?.feature_flags, "tasks_enabled") && can("tasks.view.assigned")
  if (!enabled || !user) return null
  return <MyTasksContent key={user.id} />
}

function MyTasksContent() {
  const t = useTranslations("tasks.widget")
  const format = useFormatter()

  const [tasks, setTasks] = useState<Task[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(false)
  const [completing, setCompleting] = useState<Task | null>(null)
  const request = useRef(0)
  const invalidateRequests = useCallback(() => { request.current++ }, [])

  const load = useCallback(async () => {
    const current = ++request.current
    setIsLoading(true)
    setError(false)
    try {
      const data = await getTasks({ view: "my_work", per_page: 6 })
      if (current === request.current) setTasks(data.tasks)
    } catch {
      if (current === request.current) setError(true)
    } finally {
      if (current === request.current) setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => { void load() }, 0)
    return () => { clearTimeout(timer); invalidateRequests() }
  }, [load, invalidateRequests])

  return (
    <ApolloPanel
      title={t("title")}
      actions={
        <Link
          href="/tasks"
          className="inline-flex items-center gap-0.5 text-[12.5px] font-medium text-primary hover:underline"
        >
          {t("viewAll")}
          <IconChevronRight className="size-3.5" />
        </Link>
      }
    >
      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : error ? (
        <div role="alert" className="space-y-2 py-4 text-center">
          <p className="text-sm text-destructive">{t("loadFailed")}</p>
          <Button variant="outline" size="sm" onClick={load}>{t("retry")}</Button>
        </div>
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-center gap-1.5 py-6 text-center">
          <IconListCheck className="size-6 text-muted-foreground/50" />
          <p className="text-[12.5px] text-muted-foreground">{t("empty")}</p>
        </div>
      ) : (
        <ul className="divide-y">
          {tasks.map((task) => (
            <li key={task.id} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-[13px] font-medium">{task.title}</span>
                  {task.priority === "high" && (
                    <Badge className="border-0 bg-red-500/10 px-1.5 text-[10px] text-red-600 dark:text-red-400">
                      {t("high")}
                    </Badge>
                  )}
                </div>
                {task.due_at && (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 text-[11.5px]",
                      task.is_overdue
                        ? "font-medium text-red-600 dark:text-red-400"
                        : "text-muted-foreground"
                    )}
                  >
                    {task.is_overdue && <IconAlertTriangle className="size-3" />}
                    {format.dateTime(new Date(task.due_at), { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                )}
              </div>
              {task.can_complete !== false && <Button
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-2 text-[12px]"
                disabled={!!task.blocked_reason}
                onClick={() => { if (!task.blocked_reason) setCompleting(task) }}
              >
                <IconCheck className="size-3.5" />
                {t("done")}
              </Button>}
            </li>
          ))}
        </ul>
      )}
      {completing && <TaskCompletionDialog key={completing.id} task={completing} onClose={() => setCompleting(null)} onSaved={load} />}
    </ApolloPanel>
  )
}

export function MyTasksWidget() {
  const flags = useAuthStore((state) => state.user?.feature_flags)
  return flags?.tasks_enabled && flags?.lead_follow_through_enabled && flags?.agent_work_queue_enabled
    ? <WorkflowMyTasksWidget />
    : <LegacyMyTasksWidget />
}
